import { useEffect, useState } from "react";
import { FormattedMessage as FM } from "react-intl";

import { CheckCircleIcon } from "components/Icons";
import { cn } from "tools/ui";
import SetupStep from "./SetupStep";
import {
  fetchSetupChecks,
  readQrPrinted,
  writeQrPrinted,
} from "../helpers/setupGuide";
import { SETUP_STEPS } from "../constants";

function SetupGuide({ onOpen }) {
  const [checks, setChecks] = useState(null);
  const [qrPrinted, setQrPrinted] = useState(readQrPrinted);
  const [showSteps, setShowSteps] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const result = await fetchSetupChecks();
      if (!cancelled) setChecks(result);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleQrPrinted(event) {
    const { checked } = event.target;
    setQrPrinted(checked);
    writeQrPrinted(checked);
  }

  if (!checks) return null;

  const steps = SETUP_STEPS.map((step) => ({
    ...step,
    done: step.manual ? qrPrinted : Boolean(checks[step.id]),
  }));
  const total = steps.length;
  const done = steps.filter((step) => step.done).length;
  const allDone = done === total;
  const nextId = steps.find((step) => !step.done)?.id;
  const expanded = !allDone || showSteps;

  return (
    <section
      className={cn(
        "rounded-lg border border-neutral-200 bg-white p-4 shadow-sm",
        allDone && "order-last",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex min-w-0 items-center gap-2">
          {allDone && (
            <CheckCircleIcon width={20} className="shrink-0 fill-emerald-600" />
          )}
          <h2 className="font-semibold text-neutral-800">
            <FM
              id={
                allDone ? "melhous.dash.setup.done" : "melhous.dash.setup.title"
              }
            />
          </h2>
        </div>
        <span className="text-sm text-neutral-500">
          <FM id="melhous.dash.setup.progress" values={{ done, total }} />
        </span>
      </div>
      {!allDone && (
        <>
          <p className="mt-1 text-sm text-neutral-500">
            <FM id="melhous.dash.setup.help" />
          </p>
          <div
            aria-hidden="true"
            className="mt-3 h-2 overflow-hidden rounded-full bg-neutral-100"
          >
            <div
              className="h-full rounded-full bg-sky-900 transition-all"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
        </>
      )}
      {allDone && (
        <button
          type="button"
          aria-expanded={showSteps}
          onClick={() => setShowSteps(!showSteps)}
          className="mt-1 text-sm font-medium text-sky-900 underline"
        >
          <FM
            id={
              showSteps
                ? "melhous.dash.setup.hide_steps"
                : "melhous.dash.setup.show_steps"
            }
          />
        </button>
      )}
      {expanded && (
        <ol className="mt-2 divide-y divide-neutral-100">
          {steps.map((step, idx) => (
            <SetupStep
              key={step.id}
              step={step}
              number={idx + 1}
              isNext={step.id === nextId}
              qrPrinted={qrPrinted}
              onQrPrinted={handleQrPrinted}
              onOpen={onOpen}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

export default SetupGuide;
